// The one icon primitive for the app: every UI glyph is a coolicons "Line"
// icon (icons/line/<Name>.svg), drawn from the generated coolicons-data.js.
//
//   <CoolIcon name="Chevron_Right" size={16} className="text-[#9A9A97]" />
//
// Sizing: `size` (square) or explicit width/height, or Tailwind w-/h- classes.
// Weight: `strokeWidth` is in 24-box units (coolicons' native weight is 2). To
//   keep a legacy glyph's visual weight, scale its old stroke by 24/viewBox —
//   a 16-box icon at 1.4 becomes 2.1 here and renders the same pixel stroke.
// Color: strokes inherit currentColor, so text-* classes / `style={{color}}` /
//   an explicit `stroke` prop all work.
// `fit`: crop the viewBox to the glyph's tight bounds so it fills the element —
//   for tiny non-square slots (an 8×6 check, a 6×9 chevron) where the 24-box
//   would leave the glyph at half size.
// Add a new icon name → run `python3 scripts/build_coolicons.py` to pull it in.
import { createElement } from 'react';
import { ICONS, BOUNDS } from './coolicons-data';

export const hasIcon = (name) => Boolean(ICONS[name]);

export default function CoolIcon({
    name,
    size,
    width,
    height,
    strokeWidth = 2,
    fit = false,
    className = '',
    ...rest
}) {
    const shapes = ICONS[name];
    if (!shapes) {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn(`CoolIcon: unknown icon "${name}" — add it and run scripts/build_coolicons.py`);
        }
        return null;
    }
    let viewBox = '0 0 24 24';
    if (fit && BOUNDS[name]) {
        const [x0, y0, x1, y1] = BOUNDS[name];
        const pad = strokeWidth / 2;
        viewBox = `${x0 - pad} ${y0 - pad} ${x1 - x0 + strokeWidth} ${y1 - y0 + strokeWidth}`;
    }
    const w = width ?? size;
    const h = height ?? size;
    return (
        <svg
            viewBox={viewBox}
            width={w}
            height={h}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden="true"
            {...rest}
        >
            {shapes.map(([tag, attrs], i) => createElement(tag, { key: i, ...attrs }))}
        </svg>
    );
}
